import { MappingOperation, MappingOperationResult, MappingTarget, MappingType } from 'services/api/admin/mappings';

export type PendingOperation = MappingOperation & {
  label?: string | null;
  conflict?: { current: MappingTarget | null; currentLabel: string | null } | null;
  error?: string | null;
};

export const TYPE_NOUN: Record<MappingType, string> = {
  groups: 'group',
  plan_types: 'plan type',
  group_types: 'group type',
  episodes: 'episode type',
  locations: 'location',
};

export const TYPE_TITLE: Record<MappingType, string> = {
  groups: 'Group Mappings',
  plan_types: 'Plan Type Mappings',
  group_types: 'Group Type Mappings',
  episodes: 'Episode Mappings',
  locations: 'Location Mappings',
};

export function normalizeKey(key: string) {
  return key.trim().replace(/\s+/g, ' ').toLowerCase();
}

function keySlot(type: MappingType, key: string) {
  const k = normalizeKey(key);
  return k === '*' ? `${type}|fallback` : `${type}|map|${k}`;
}

export function slotOf(op: MappingOperation) {
  switch (op.op) {
    case 'set':
    case 'acknowledge':
    case 'dont_map':
    case 'remove':
      return keySlot(op.type, op.key);
    case 'dismiss':
      return `${op.type}|dismiss|${op.attrValueId}`;
    case 'instructions':
      return `${op.type}|instructions`;
    case 'fallback':
      return `${op.type}|fallback`;
  }
}

export function upsertOperation(ops: PendingOperation[], op: PendingOperation) {
  const slot = slotOf(op);
  const rest = ops.filter((o) => slotOf(o) !== slot);
  return [...rest, op];
}

export function removeSlot(ops: PendingOperation[], slot: string) {
  return ops.filter((o) => slotOf(o) !== slot);
}

export function mappingOpFor(ops: PendingOperation[], type: MappingType, key: string) {
  const slot = keySlot(type, key);
  return ops.find((o) => slotOf(o) === slot && o.op !== 'dismiss') ?? null;
}

export function toWire(ops: PendingOperation[]): MappingOperation[] {
  return ops.map(({ label: _label, conflict: _conflict, error: _error, ...op }) =>
    'expected' in op ? ({ ...op, expected: op.expected ?? null } as MappingOperation) : (op as MappingOperation)
  );
}

export function withResults(ops: PendingOperation[], results: MappingOperationResult[]): PendingOperation[] {
  return ops.map((op, index) => {
    const result = results.find((r) => r.index === index);
    if (!result || result.status === 'ok') return { ...op, conflict: null, error: null };
    if (result.status === 'conflict') {
      return {
        ...op,
        conflict: { current: result.current ?? null, currentLabel: result.currentLabel ?? null },
        error: null,
      };
    }
    const error =
      result.code === 'not_in_network'
        ? "This group isn't in the client's network. Change the mapping and confirm adding it to the network."
        : (result.message ?? "This change couldn't be saved.");
    return { ...op, conflict: null, error };
  });
}

export function keepMine(op: PendingOperation): PendingOperation {
  if (!op.conflict || !('expected' in op)) return op;
  return { ...op, expected: op.conflict.current, conflict: null } as PendingOperation;
}

export function targetText(target: MappingTarget | null | undefined) {
  if (target == null) return '';
  return Array.isArray(target) ? target.join(', ') : target;
}

export function percent(confidence: number | null | undefined) {
  return confidence == null ? null : `${Math.round(confidence * 100)}%`;
}
